import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiPortal, EuiTab, EuiTabs, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { useGetBillingProviderQuery } from 'api/rtkQueryApi/platform/coreBillingApi'
import { RootState } from 'app/rootReducer'
import { close } from 'features/organization/organizationSlice'
import { EndOfMonthReport } from 'features/reports/EndOfMonthReport'
import { ForecastReport } from 'features/reports/ForecastReport'
import { MonthlyLedgerReport } from 'features/reports/MonthlyLedgerReport'
import { useCampaignApproval } from 'hooks/useCampaignApproval'
import { useEnterpriseSupport } from 'hooks/useEnterpriseSupport'

import { BillingProvider, PermissionType, UserType, WhiteLabel } from '../../api/interfaces'
import { useOrganizationPermissions } from '../../providers/OrganizationPermissionsContext'
import { useWhiteLabel } from '../../whiteLabel/WhiteLabelContext'
import { AgencySettingsDrawer } from './AgencySettingsDrawer'
import BalanceLedgerDrawer from './BalanceLedgerDrawer'
import { ChangePasswordDrawer } from './ChangePasswordDrawer'
import InvoicesDrawer from './InvoicesDrawer'
import OrganizationDrawer from './OrganizationDrawer'
import PaymentMethodsDrawer from './PaymentMethodsDrawer'
import PlatformAccessDrawer from './PlatformAccessDrawer'
import UsersDrawer from './UsersDrawer'

interface TabProps {
  name: string
  id: string
}

export const AdministrationFlyout: React.FC = () => {
  const dispatch = useDispatch()
  const { currentOrganization, currentUser, isStaffUser } = useSelector((state: RootState) => state.app)
  const organization = useSelector((state: RootState) => state.organization)
  const [selectedTabId, setSelectedTabId] = useState('details')
  const whiteLabel = useWhiteLabel()
  const { hasPermission } = useOrganizationPermissions()
  const isEnterpriseOrganization = useEnterpriseSupport()
  const { userType } = useCampaignApproval()
  const canViewInfusionReports = isStaffUser || userType === UserType.PlatformAdmin || currentUser?.roles.includes('infusion-pro')
  const canManageAgencySettings = whiteLabel?.getWhiteLabel() === WhiteLabel.AgencyV2 && hasPermission(PermissionType.AgencyReserve) && !hasPermission(PermissionType.HideAgencyReserve)

  const { data: billingProvider } = useGetBillingProviderQuery({ accountId: '' }, { skip: !currentOrganization })

  const closeFlyout = () => dispatch(close())

  const tabsArray: Array<TabProps> = [
    ...(canManageAgencySettings
      ? [
          {
            name: 'Agency Settings',
            id: 'details'
          }
        ]
      : [
          {
            name: 'Contact Details',
            id: 'details'
          }
        ]),
    {
      name: 'Subscription',
      id: 'subscription'
    },
    {
      name: 'Change Password',
      id: 'changePassword'
    },
    ...(billingProvider && billingProvider === BillingProvider.Balance
      ? [
          {
            name: 'Balance Ledger',
            id: 'balanceLedger'
          }
        ]
      : [
          {
            name: 'Payment Methods',
            id: 'paymentMethods'
          }
        ]),
    {
      name: 'Users',
      id: 'users'
    },
    {
      name: 'Invoices',
      id: 'invoices'
    },
    ...(isEnterpriseOrganization && canViewInfusionReports
      ? [
          {
            name: 'Forecast',
            id: 'forecast'
          },
          {
            name: 'End of Month',
            id: 'endOfMonth'
          },
          {
            name: 'Ledger',
            id: 'ledger'
          }
        ]
      : [])
  ]

  let flyout
  if (currentOrganization && organization.isOpen) {
    flyout = (
      <EuiPortal>
        <AcFlyout onClose={closeFlyout} size='l' aria-labelledby='flyoutLargeTitle' ownFocus hideCloseButton>
          <EuiFlyoutHeader hasBorder>
            <EuiTitle>
              <h4>Platform Management</h4>
            </EuiTitle>
            <EuiTabs style={{ marginBottom: '-25px' }}>
              {tabsArray.map((t, index) => (
                <EuiTab key={index} isSelected={t.id === selectedTabId} onClick={() => setSelectedTabId(t.id)}>
                  {t.name}
                </EuiTab>
              ))}
            </EuiTabs>
          </EuiFlyoutHeader>
          <EuiFlyoutBody>
            {selectedTabId === 'details' && (canManageAgencySettings ? <AgencySettingsDrawer /> : <OrganizationDrawer />)}
            {selectedTabId === 'subscription' && <PlatformAccessDrawer />}
            {selectedTabId === 'paymentMethods' && <PaymentMethodsDrawer />}
            {selectedTabId === 'balanceLedger' && <BalanceLedgerDrawer />}
            {selectedTabId === 'users' && <UsersDrawer />}
            {selectedTabId === 'invoices' && <InvoicesDrawer />}
            {selectedTabId === 'changePassword' && <ChangePasswordDrawer />}
            {isEnterpriseOrganization && selectedTabId === 'forecast' && <ForecastReport />}
            {isEnterpriseOrganization && selectedTabId === 'endOfMonth' && <EndOfMonthReport />}
            {isEnterpriseOrganization && selectedTabId === 'ledger' && <MonthlyLedgerReport />}
          </EuiFlyoutBody>
          <EuiFlyoutFooter>
            <EuiFlexGroup direction='row' alignItems='center'>
              <EuiFlexItem grow={false}>
                <EuiButton onClick={closeFlyout}>
                  Close
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlyoutFooter>
        </AcFlyout>
      </EuiPortal>
    )
  }

  return <React.Fragment>{flyout}</React.Fragment>
}

export default AdministrationFlyout
